/**
 * Single namespace for all development-only browser helpers.
 *
 * Instead of attaching flat handlers to `window` (`__enableMsw`, `__enableReactScan`,
 * …), every dev patch registers its helpers under one object:
 *
 *   window.__confidenceDev.enableReactScan()
 *   window.__confidenceDev.enableMsw()
 *   …
 *
 * The namespace only exists in dev builds, so production code can feature-detect
 * it without importing this package:
 *
 *   window.__confidenceDev?.logGraphqlOperation?.(operationName);
 */
import type { UiLibraryHighlightRenderOptions } from '../uiLibraries/highlightOverlay';
import type { UiLibraryPickOptions } from '../uiLibraries/pickElement';
import type {
  UiLibraryNode,
  UiLibraryReport,
  UiLibraryScanOptions,
} from '../uiLibraries/scanUiLibraries';

type ReactSourceMap = Record<
  string,
  Array<{ tag: string; text: string; role: string; ariaLabel: string }>
>;

type ReactComponentSourceMap = Record<
  string,
  Array<{ component: string; type: 'component' | 'host'; text: string }>
>;

type ReactSourceAncestors = Array<{
  component: string;
  source: string;
  host: boolean;
}>;

export interface ConfidenceDevApi {
  // Local feature flag overrides (featureFlags)
  setFeatureFlags: (flags: Record<string, boolean>) => void;
  clearFeatureFlags: () => void;

  // React dev source mapping (patchDevSource)
  enableReactDevSource: () => void;
  disableReactDevSource: () => void;
  getReactSourceMap: () => ReactSourceMap;
  getReactComponentSourceMap: () => ReactComponentSourceMap;
  getReactSourceAncestors: (
    element: Element,
    limit?: number,
  ) => ReactSourceAncestors;
  reactViz: (element: Element, fullVersion?: number) => void;

  // Component profiling (patchProfiler)
  profileReactComponents: (
    element: Element,
    focusMode?: boolean,
    filter?: string[],
  ) => void;
  clearReactProfile: () => void;

  // react-scan overlay (reactScan)
  enableReactScan: () => void;
  disableReactScan: () => void;

  // Mock Service Worker (msw)
  enableMsw: () => void;
  disableMsw: () => void;

  // GraphQL operation logging (graphqlLog)
  enableGraphqlLog: () => void;
  disableGraphqlLog: () => void;
  logGraphqlOperation: (operationName?: string) => void;

  // Encore/MUI DOM inspection (uiLibraries)
  getUiLibraries: (
    target?: Element | string | null,
    options?: UiLibraryScanOptions,
  ) => UiLibraryReport;
  highlightUiLibraries: (
    target?: Element | string | null,
    options?: UiLibraryScanOptions & UiLibraryHighlightRenderOptions,
  ) => UiLibraryReport;
  clearUiLibraryHighlight: () => void;
  pickUiLibrary: (
    options?: UiLibraryPickOptions,
  ) => Promise<UiLibraryNode | null>;
  cancelUiLibraryPick: () => void;
}

declare global {
  interface Window {
    __confidenceDev?: Partial<ConfidenceDevApi>;
  }
}

/**
 * Merges the given helpers into `window.__confidenceDev`, creating the namespace
 * on first call. Safe to call from any dev patch; later calls add to the same object.
 */
export const registerConfidenceDev = (api: Partial<ConfidenceDevApi>): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.__confidenceDev = { ...window.__confidenceDev, ...api };
};

const STORAGE_KEY_PREFIX = '__confidenceDev:';

/**
 * Builds a namespaced localStorage key so all dev-tooling keys share one prefix
 * instead of being flat, e.g. `confidenceDevStorageKey('react-scan')` →
 * `'__confidenceDev:react-scan'`.
 */
export const confidenceDevStorageKey = (name: string): string =>
  `${STORAGE_KEY_PREFIX}${name}`;
